import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { 
  Database, 
  Hash, 
  Type, 
  Calendar, 
  BarChart2, 
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Search,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';
import './DataSummary.css';
import { AnimatedCard, AnimatedNumber, useAnimateOnMount } from '../common/AnimationUtils';
import AILoadingOverlay from '../common/AILoadingOverlay';

// const API_BASE_URL = 'http://localhost:8000';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Responsive hook
const useWindowWidth = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
};

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  variable_name: 200,
  data_type: 100,
  domain: 110,
  null_count: 130,
  unique_count: 100,
  completeness: 150,
  quality_score: 120,
  quality_issues: 200,
  min: 100,
  max: 100,
  mean: 100,
  median: 100,
  std_dev: 100,
  q1: 80,
  q3: 80,
  iqr: 80,
  skewness: 90,
  normality: 100,
  expand: 50
};

// Cache key prefix for localStorage
const CACHE_KEY_PREFIX = 'dataquad_datasummary_';

const DataSummary = ({ 
  
  primaryData, 
  backcheckData, 
  projectId,  // NEW: Project ID for server-side data summary (Phase 2)
  primaryFileName = 'Primary Survey',
  backcheckFileName = 'Back-Check Data',
  xlsformData = null  // NEW: parsed XLSForm state from App ({ parseResult, matchingResult, fileName })

}) => {
  
  const { isDarkMode } = useTheme();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const [activeDataset, setActiveDataset] = useState('primary');
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'variable_name', direction: 'asc' });
  const [filterType, setFilterType] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0); // Increment to force re-fetch
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null); // Track when data was analyzed
  const barsReady = useAnimateOnMount(500);
  
  // Column resize state
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const [resizing, setResizing] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const tableContainerRef = useRef(null);

  // Cache utilities
  const getCacheKey = useCallback((dataset, fileName) => {
    // Create a unique key based on dataset type and filename
    return `${CACHE_KEY_PREFIX}${dataset}_${fileName?.replace(/[^a-zA-Z0-9]/g, '_') || 'unknown'}`;
  }, []);

  const getFromCache = useCallback((dataset, fileName) => {
    try {
      const key = getCacheKey(dataset, fileName);
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading from cache:', e);
    }
    return null;
  }, [getCacheKey]);

  const saveToCache = useCallback((dataset, fileName, data) => {
    try {
      const key = getCacheKey(dataset, fileName);
      const cacheData = {
        ...data,
        timestamp: Date.now(),
        analyzedAt: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Error saving to cache:', e);
    }
  }, [getCacheKey]);

  const clearCache = useCallback((dataset, fileName) => {
    try {
      const key = getCacheKey(dataset, fileName);
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('Error clearing cache:', e);
    }
  }, [getCacheKey]);

  // Handle refresh button click
  const handleRefreshAnalysis = useCallback(() => {
    const fileName = activeDataset === 'primary' ? primaryFileName : backcheckFileName;
    clearCache(activeDataset, fileName);
    setForceRefresh(prev => prev + 1);
  }, [activeDataset, primaryFileName, backcheckFileName, clearCache]);

  // Color scheme matching the app's dark mode (from your screenshots)
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#141516' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#141516' : '#FAFAFA',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
    borderLight: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.05)',
    text: isDarkMode ? '#f0ede4' : '#1a1a1a',
    textSecondary: isDarkMode ? '#a8a49a' : '#555550',
    textMuted: isDarkMode ? '#6b6862' : '#908c82',
    headerBg: isDarkMode ? '#0D0E0E' : '#F9FAFB',
    rowHover: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.025)',
    rowAlt: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#FAFAFA',
    accent: '#2563EB',
    accentSecondary: '#D97706',
    success: '#047857',
    warning: '#b45309',
    error: '#dc2626',
    purple: '#8b6c9b',
    russet: '#b57558',
  };

  // Determine current data based on toggle
  const currentData = activeDataset === 'primary' ? primaryData : backcheckData;
  const currentFileName = activeDataset === 'primary' ? primaryFileName : backcheckFileName;

  // Column resize handlers
  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  }, [columnWidths]);

  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(60, startWidth + diff);
    
    setColumnWidths(prev => ({
      ...prev,
      [resizing]: newWidth
    }));
  }, [resizing, startX, startWidth]);

  const handleResizeEnd = useCallback(() => {
    setResizing(null);
  }, []);

  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // Fetch summary when data or dataset changes (with caching)
  useEffect(() => {
    const fetchSummary = async () => {
      const useServerSide = !!projectId;
      
      // Guard: need either projectId (for v2) or data (for v1)
      if (!useServerSide && (!currentData || (!currentData.full_data && !currentData.data) || 
          (currentData.full_data || currentData.data || []).length === 0)) {
        setSummaryData(null);
        setLastAnalyzedAt(null);
        return;
      }
      
      // For server-side: just need to know the dataset exists
      if (useServerSide && !currentData) {
        setSummaryData(null);
        setLastAnalyzedAt(null);
        return;
      }

      // Check cache first (unless forceRefresh was triggered)
      const cached = getFromCache(activeDataset, currentFileName);
      if (cached && forceRefresh === 0) {
        setSummaryData(cached);
        setLastAnalyzedAt(cached.analyzedAt || null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const token = localStorage.getItem('token');
        
        let response;
        
        if (useServerSide) {
          // V2: Server loads data from DB by project_id — no data in request body
          const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          };
          if (token) headers['Authorization'] = `Bearer ${token}`;
          if (userInfo?.email) headers['user_email'] = userInfo.email;
          
          response = await fetch(`${API_BASE_URL}/data-summary-v2/`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              project_id: projectId,
              data_file_type: activeDataset === 'primary' ? 'primary' : 'backcheck',
              file_name: currentFileName,
              data_source: activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check'
            })
          });
        } else {
          // V1 fallback: Send data in request body
          const dataToSend = currentData.full_data || currentData.data || [];
          response = await fetch(`${API_BASE_URL}/data-summary/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'user_email': userInfo?.email || '',
            },
            body: JSON.stringify({
              data: dataToSend,
              column_types: currentData.column_types || {},
              file_name: currentFileName,
              data_source: activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check'
            })
          });
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch summary: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success) {
          saveToCache(activeDataset, currentFileName, result);
          setSummaryData(result);
          setLastAnalyzedAt(new Date().toISOString());
        } else {
          setError(result.message || 'Failed to generate summary');
        }
      } catch (err) {
        console.error('Error fetching data summary:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
        // Reset forceRefresh after fetch completes so cache works on next tab switch
        if (forceRefresh > 0) {
          setForceRefresh(0);
        }
      }
    };

    fetchSummary();
  }, [currentData, activeDataset, currentFileName, forceRefresh, projectId, getFromCache, saveToCache]);

  // ── Variable labels from the parsed XLSForm ──
  // Resolution order for a dataset column:
  //   1. direct name match against the form's questions
  //   2. through the Variable Mapping (form field -> data column), which
  //      handles renamed exports like children_under5 -> children_u5
  //   3. group-prefix fallback: KoBo/ODK exports often name columns
  //      "group/field" or "group.field", so try the last path segment
  // Labels can be plain strings or {lang: text} dicts (multi-language forms).
  const variableLabels = useMemo(() => {
    const questions = xlsformData?.parseResult?.questions || {};
    const matches = xlsformData?.matchingResult?.matches || {};
    if (!Object.keys(questions).length) return {};

    const normalize = (raw) => {
      let text = raw;
      if (text && typeof text === 'object') {
        const first = Object.values(text).find(v => typeof v === 'string' && v.trim());
        text = first || '';
      }
      if (typeof text !== 'string') return '';
      // strip simple HTML that KoBo labels sometimes carry
      return text.replace(/<[^>]*>/g, '').trim();
    };

    const labelFor = (formField) => {
      const q = questions[formField];
      if (!q) return '';
      const label = normalize(q.label);
      // a label identical to the field name adds nothing
      return label && label !== formField ? label : '';
    };

    const index = {};
    // 1. every form field labels a same-named column
    Object.keys(questions).forEach(f => {
      const label = labelFor(f);
      if (label) index[f] = label;
    });
    // 2. mapping wins over name match: form field -> its mapped data column
    Object.entries(matches).forEach(([formField, dataCol]) => {
      const label = labelFor(formField);
      if (label && dataCol) index[dataCol] = label;
    });
    return index;
  }, [xlsformData]);

  const getVariableLabel = useCallback((columnName) => {
    if (!columnName) return '';
    if (variableLabels[columnName]) return variableLabels[columnName];
    // 3. group-prefix fallback
    const leaf = String(columnName).split(/[\/.]/).pop();
    return (leaf !== columnName && variableLabels[leaf]) || '';
  }, [variableLabels]);

  // Filter and sort summaries
  const filteredSummaries = useMemo(() => {
    if (!summaryData?.summaries) return [];

    let filtered = [...summaryData.summaries];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.variable_name.toLowerCase().includes(term) ||
        s.domain.toLowerCase().includes(term) ||
        s.data_type.toLowerCase().includes(term) ||
        getVariableLabel(s.variable_name).toLowerCase().includes(term)
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(s => {
        if (filterType === 'numeric') return s.data_type === 'integer' || s.data_type === 'float64';
        if (filterType === 'text') return s.data_type === 'string';
        if (filterType === 'datetime') return s.data_type === 'timestamp';
        return true;
      });
    }

    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [summaryData, searchTerm, filterType, sortConfig, getVariableLabel]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleRowExpand = (variableName) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variableName)) {
        newSet.delete(variableName);
      } else {
        newSet.add(variableName);
      }
      return newSet;
    });
  };

  const getDataTypeIcon = (dataType) => {
    switch (dataType) {
      case 'integer':
      case 'float64':
        return <Hash size={14} style={{ color: colors.textMuted }} />;
      case 'timestamp':
        return <Calendar size={14} style={{ color: colors.textMuted }} />;
      default:
        return <Type size={14} style={{ color: colors.textMuted }} />;
    }
  };

  // Dot color per domain. Used with the pc-status pattern — muted text + colored dot,
  // no pill background. Retains quick-scan category coding without visual noise.
  const getDomainDotColor = (domain) => {
    const colorMap = {
      'Number':      '#2563EB', // brand blue
      'Boolean':     '#D97706', // brand amber
      'Datetime':    '#8b6c9b', // welcome-progress purple
      'Categorical': '#b57558', // welcome-progress russet
      'Phone':       '#dc2626', // warning red
      'Email':       '#047857', // brand emerald
      'String':      '#908c82', // muted neutral
      'URL':         '#908c82',
      'UUID':        '#908c82',
    };
    return colorMap[domain] || '#908c82';
  };

  const getNormalityIndicator = (score) => {
    if (score === null || score === undefined) return null;
    
    let color, icon;
    if (score >= 70) {
      color = colors.success;
      icon = <CheckCircle size={14} />;
    } else if (score >= 40) {
      color = colors.warning;
      icon = <Minus size={14} />;
    } else {
      color = colors.error;
      icon = <AlertCircle size={14} />;
    }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color }}>
        {icon}
        <span style={{ fontSize: '12px' }}>{score}</span>
      </div>
    );
  };

  const getSkewnessIndicator = (skewness) => {
    if (skewness === null || skewness === undefined) return null;
    
    if (Math.abs(skewness) < 0.5) {
      return <Minus size={14} style={{ color: colors.success }} />;
    } else if (skewness > 0) {
      return <TrendingUp size={14} style={{ color: colors.warning }} />;
    } else {
      return <TrendingDown size={14} style={{ color: colors.warning }} />;
    }
  };

  // Smart number formatter that handles very large/small numbers
  const formatValue = (value, decimals = 2, compact = false) => {
    if (value === null || value === undefined) return '—';
    
    if (typeof value === 'number') {
      // Handle special cases
      if (isNaN(value)) return '—';
      if (!isFinite(value)) return value > 0 ? '∞' : '-∞';
      
      const absValue = Math.abs(value);
      
      // For very large numbers, use compact notation
      if (absValue >= 1e15) {
        // Use scientific notation for extremely large numbers
        return value.toExponential(2);
      } else if (absValue >= 1e12) {
        return (value / 1e12).toFixed(2) + 'T';
      } else if (absValue >= 1e9) {
        return (value / 1e9).toFixed(2) + 'B';
      } else if (absValue >= 1e6) {
        return (value / 1e6).toFixed(2) + 'M';
      } else if (absValue >= 1e4 && compact) {
        return (value / 1e3).toFixed(1) + 'K';
      } else if (Number.isInteger(value)) {
        return value.toLocaleString();
      } else {
        // For decimals, limit precision
        return value.toLocaleString(undefined, { 
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals 
        });
      }
    }
    
    // For strings (like dates), truncate if too long
    if (typeof value === 'string' && value.length > 20) {
      return value.substring(0, 17) + '...';
    }
    
    return value;
  };

  // Full number formatter for tooltips (no abbreviation)
  const formatFullValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
      if (isNaN(value) || !isFinite(value)) return String(value);
      return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
    }
    return String(value);
  };

  const exportToCSV = () => {
    if (!filteredSummaries.length) return;

    const headers = [
      'Variable Name', 'Variable Label', 'Data Type', 'Domain', 'Total', 'Nulls', 'Null %',
      'Unique', 'Completeness %', 'Quality Score', 'Quality Issues', 
      'Min', 'Max', 'Mean', 'Median', 'Std Dev',
      'Q1', 'Q3', 'IQR', 'Skewness', 'Kurtosis', 'Normality Score'
    ];

    // question text routinely contains commas and quotes, so CSV-escape it
    const csvSafe = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const rows = filteredSummaries.map(s => [
      s.variable_name,
      csvSafe(getVariableLabel(s.variable_name)),
      s.data_type,
      s.domain,
      s.total_count,
      s.null_count,
      s.null_percentage,
      s.unique_count,
      s.completeness,
      s.quality_score ?? '',
      s.quality_issues ? s.quality_issues.join('; ') : '',
      s.min_value ?? s.min_length ?? s.min_date ?? '',
      s.max_value ?? s.max_length ?? s.max_date ?? '',
      s.mean ?? s.mean_length ?? '',
      s.median ?? '',
      s.std_dev ?? '',
      s.q1 ?? '',
      s.q3 ?? '',
      s.iqr ?? '',
      s.skewness ?? '',
      s.kurtosis ?? '',
      s.normality_score ?? ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data_summary_${activeDataset}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render empty state - Style
  if (!primaryData && !backcheckData) {
    return (
      <div className="ds-empty-state" style={{
        backgroundColor: colors.background,
        color: colors.textSecondary,
        marginTop: '40px',
      }}>
        <Database size={56} className="ds-empty-icon" />
        <h2 className="ds-empty-title" style={{ color: colors.text }}>
          No Data Loaded
        </h2>
        <p className="ds-empty-text">
          Load your primary survey data or back-check data to view variable summaries and quality metrics.
        </p>
      </div>
    );
  }

  return (
    <div className="data-summary-container" style={{
      backgroundColor: colors.background,
      minHeight: '100%', 
      width: '100%',
      maxWidth: '100%',
      padding: isMobile ? '24px 12px 24px 12px' : '40px 60px 24px 60px',
      //marginLeft: '70px',
    }}>
      <AILoadingOverlay
        isVisible={isLoading}
        context="data_loading"
        title="Analyzing Data Summary"
        subtitle="Computing statistics and quality metrics for your dataset"
        realTimeStats={{
          totalRows: currentData?.total_rows || currentData?.full_data?.length || currentData?.data?.length || 0,
          columnsAnalyzed: currentData?.column_types 
            ? Object.keys(currentData.column_types).length 
            : (currentData?.total_variables || currentData?.columns?.length || 0),
        }}
        showDetailedLogs={true}
      />
      
      {/* Header Section */}
      <div className="ds-header">
        <div className="ds-header-top">
          <div>
            <h1 className="ds-title" style={{ fontSize: '22px',
              fontWeight: '500', color: colors.text }}>
              Data Summary
            </h1>
            <p className="ds-subtitle" style={{ color: colors.textSecondary }}>
              Statistical overview of all variables in your dataset
            </p>
          </div>
          
          {/* Tabs */}
          <div className="ds-tabs-container" style={{
            marginTop: isMobile ? '16px' : '35px',
          }}>
            <button
              onClick={() => setActiveDataset('primary')}
              disabled={!primaryData}
              className={`ds-tab ${activeDataset === 'primary' ? 'active' : ''}`}
              style={{ color: colors.text }}
            >
              <Database size={13} />
              <span>Primary Survey</span>
              {primaryData && (
                <span className="ds-tab-count">
                  {primaryData.total_rows?.toLocaleString()} rows
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveDataset('backcheck')}
              disabled={!backcheckData}
              className={`ds-tab ${activeDataset === 'backcheck' ? 'active' : ''}`}
              style={{ color: colors.text }}
            >
              <CheckCircle size={13} />
              <span>Back-Check</span>
              {backcheckData && (
                <span className="ds-tab-count">
                  {backcheckData.total_rows?.toLocaleString()} rows
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

             
      {/* Stats Overview Cards - Uniqueness KEY METRICS layout */}
      {summaryData && (
        <AnimatedCard delay={0} translateY={12}>
        <div className="ds-stats-grid">
          {[
            { label: 'total variables', value: summaryData.total_variables, subtitle: 'Columns detected', Icon: BarChart2 },
            { label: 'total observations', value: summaryData.total_rows, subtitle: 'Rows in dataset', Icon: Database },
            { label: 'numeric variables', value: summaryData.summaries.filter(s => s.data_type === 'integer' || s.data_type === 'float64').length, subtitle: 'Integer & float', Icon: Hash },
            { label: 'text variables', value: summaryData.summaries.filter(s => s.data_type === 'string').length, subtitle: 'String columns', Icon: Type },
            { label: 'date variables', value: summaryData.summaries.filter(s => s.data_type === 'timestamp').length, subtitle: 'Timestamp columns', Icon: Calendar },
          ].map((stat, idx) => {
            // Neutral descriptive counts — no severity
            const iconColor = isDarkMode ? '#60a5fa' : '#2563EB';
            return (
              <div key={idx} className="ds-stat-card nsx-metric-card" style={{
                position: 'relative',
                padding: '18px',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                borderRadius: '8px',
                border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                overflow: 'hidden',
                fontFamily: "'Outfit', -apple-system, sans-serif",
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  {/* Icon chip */}
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '5px',
                    background: 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, color: iconColor,
                  }}>
                    <stat.Icon size={16} strokeWidth={1.75} />
                  </div>
                  {/* Centered text block */}
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                    <div style={{
                      fontSize: '10px', fontWeight: 500,
                      letterSpacing: '0.16em', textTransform: 'lowercase',
                      color: colors.textSecondary,
                      marginBottom: '10px',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {stat.label}
                    </div>
                    <div className="metric-value ds-stat-value" style={{
                      fontSize: '32px', fontWeight: 500,
                      letterSpacing: '-0.02em',
                      fontVariantNumeric: 'tabular-nums',
                      color: colors.text,
                      lineHeight: 1.05, marginBottom: '6px',
                    }}>
                      <AnimatedNumber value={stat.value} duration={800} delay={300} />
                    </div>
                    <div style={{
                      fontSize: '11.5px', fontWeight: 400,
                      letterSpacing: '-0.005em',
                      color: colors.textMuted || colors.textSecondary,
                      lineHeight: 1.4,
                    }}>
                      {stat.subtitle}
                    </div>
                  </div>
                  {/* Phantom spacer — matches icon width so text is truly card-centered */}
                  <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
                </div>
              </div>
            );
          })}
        </div>
        </AnimatedCard>
      )}

      {/* Controls Row - Style */}
      <AnimatedCard delay={80} translateY={12}>
      <div className="ds-controls">
        {/* Search */}
        <div className="ds-controls-left">
          <div className="ds-search" style={{
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
          }}>
            <Search size={16} className="ds-search-icon" style={{ color: colors.textSecondary }} />
            <input
              type="text"
              placeholder="Search variables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ color: colors.text }}
            />
          </div>
        </div>

        {/* Filter and Actions */}
        <div className="ds-controls-right">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="ds-select"
            style={{
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
            }}
          >
            <option value="all">All Types</option>
            <option value="numeric">Numeric Only</option>
            <option value="text">Text Only</option>
            <option value="datetime">Datetime Only</option>
          </select>

          <button
            onClick={() => setShowDetailedView(!showDetailedView)}
            className="ds-btn"
            style={{
              border: `1px solid ${colors.border}`,
              backgroundColor: showDetailedView ? (isDarkMode ? '#777777' : '#b4b4b4') : colors.cardBg,
              color: showDetailedView ? '#000000' : colors.text,
            }}
          >
            <BarChart2 size={15} />
            {showDetailedView ? 'Detailed' : 'Detailed'}
          </button>

          <button
            onClick={exportToCSV}
            disabled={!filteredSummaries.length}
            className="ds-btn"
            style={{
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
            }}
          >
            <Download size={15} />
            Export
          </button>

          <button
            onClick={handleRefreshAnalysis}
            disabled={isLoading || !currentData}
            title="Re-analyze data (clears cached results)"
            className="ds-btn"
            style={{
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
            }}
          >
            <RefreshCw size={15} className={isLoading ? 'spin' : ''} />
            Refresh
          </button>

          {/* Last analyzed indicator */}
          {lastAnalyzedAt && !isLoading && (
            <span className="ds-timestamp" style={{ color: colors.textMuted }}>
              <CheckCircle size={12} style={{ color: colors.success }} />
              Cached: {new Date(lastAnalyzedAt).toLocaleString(undefined, { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          )}
        </div>
      </div>
      </AnimatedCard>

      {/* Loading State - Style */}
      {isLoading && (
        <div className="ds-loading" style={{ color: colors.textSecondary }}>
          <RefreshCw size={20} className="spin" />
          Analyzing data...
        </div>
      )}

      {/* Error State - Style */}
      {error && (
        <div className="ds-error" style={{
          backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#FEE2E2',
          color: colors.error,
          border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#FECACA'}`
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Summary Table - Style */}
      {!isLoading && summaryData && (
        <AnimatedCard delay={160} translateY={12}>
        <div 
          ref={tableContainerRef}
          className="ds-table-container"
          style={{
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div 
            className="ds-table-wrapper"
          >
            <table className="ds-table" style={{ color: colors.text }}>
              <thead>
                <tr>
                  <HeaderCell 
                    columnKey="variable_name" 
                    width={columnWidths.variable_name}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                    isSticky={true}
                  >
                    Variable ↕
                  </HeaderCell>
                  <HeaderCell 
                    columnKey="data_type" 
                    width={columnWidths.data_type}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                  >
                    Type
                  </HeaderCell>
                  <HeaderCell 
                    columnKey="domain" 
                    width={columnWidths.domain}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                  >
                    Domain
                  </HeaderCell>
                  <HeaderCell 
                    columnKey="null_count" 
                    width={columnWidths.null_count}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                  >
                    Nulls
                  </HeaderCell>
                  <HeaderCell 
                    columnKey="unique_count" 
                    width={columnWidths.unique_count}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                  >
                    Unique
                  </HeaderCell>
                  <HeaderCell 
                    columnKey="completeness" 
                    width={columnWidths.completeness}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                  >
                    Complete %
                  </HeaderCell>
                  <HeaderCell 
                    columnKey="quality_score" 
                    width={columnWidths.quality_score}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResizeStart={handleResizeStart}
                    colors={colors}
                  >
                    Quality
                  </HeaderCell>
                  {showDetailedView && (
                    <>
                      <HeaderCell columnKey="quality_issues" width={columnWidths.quality_issues} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Issues</HeaderCell>
                      <HeaderCell columnKey="min" width={columnWidths.min} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Min</HeaderCell>
                      <HeaderCell columnKey="max" width={columnWidths.max} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Max</HeaderCell>
                      <HeaderCell columnKey="mean" width={columnWidths.mean} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Mean</HeaderCell>
                      <HeaderCell columnKey="median" width={columnWidths.median} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Median</HeaderCell>
                      <HeaderCell columnKey="std_dev" width={columnWidths.std_dev} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Std Dev</HeaderCell>
                      <HeaderCell columnKey="q1" width={columnWidths.q1} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Q1</HeaderCell>
                      <HeaderCell columnKey="q3" width={columnWidths.q3} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Q3</HeaderCell>
                      <HeaderCell columnKey="iqr" width={columnWidths.iqr} sortable={false} onResizeStart={handleResizeStart} colors={colors}>IQR</HeaderCell>
                      <HeaderCell columnKey="skewness" width={columnWidths.skewness} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Skew</HeaderCell>
                      <HeaderCell columnKey="normality" width={columnWidths.normality} sortable={false} onResizeStart={handleResizeStart} colors={colors}>Normality</HeaderCell>
                    </>
                  )}
                  <th style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 20,
                    backgroundColor: colors.headerBg,
                    padding: '14px 8px',
                    borderBottom: `1px solid ${colors.border}`,
                    width: 50,
                    minWidth: 50
                  }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredSummaries.map((summary, index) => {
                  const isExpanded = expandedRows.has(summary.variable_name);
                  const domainColor = getDomainDotColor(summary.domain);
                  const rowBgColor = index % 2 === 0 ? colors.cardBg : colors.rowAlt;
                  
                  return (
                    <React.Fragment key={summary.variable_name}>
                      <tr 
                        className="data-summary-row"
                        style={{
                          backgroundColor: index % 2 === 0 ? 'transparent' : colors.rowAlt,
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <td style={cellStyle(colors, columnWidths.variable_name, true, rowBgColor)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {getDataTypeIcon(summary.data_type)}
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontWeight: '500', color: colors.text }}>{summary.variable_name}</span>
                              {getVariableLabel(summary.variable_name) && (
                                <div
                                  title={getVariableLabel(summary.variable_name)}
                                  style={{
                                    fontSize: '11px',
                                    color: colors.textMuted,
                                    marginTop: '2px',
                                    maxWidth: (columnWidths.variable_name - 40) + 'px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {getVariableLabel(summary.variable_name)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={cellStyle(colors, columnWidths.data_type)}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            backgroundColor: isDarkMode ? '#1F1F23' : '#F3F4F6',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            color: colors.textSecondary
                          }}>
                            {summary.data_type}
                          </span>
                        </td>
                        <td style={cellStyle(colors, columnWidths.domain)}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '7px',
                            fontSize: '11px',
                            fontWeight: 500,
                            letterSpacing: '0.06em',
                            textTransform: 'lowercase',
                            color: colors.textSecondary,
                          }}>
                            <span style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: domainColor,
                              flexShrink: 0,
                            }} />
                            {summary.domain}
                          </span>
                        </td>
                        <td style={cellStyle(colors, columnWidths.null_count)}>
                          <div>
                            <span style={{ fontWeight: '500', color: colors.text }}>{summary.null_count.toLocaleString()}</span>
                            <span style={{ 
                              marginLeft: '4px', 
                              fontSize: '11px', 
                              color: summary.null_percentage > 10 ? colors.error : colors.textMuted
                            }}>
                              ({summary.null_percentage}%)
                            </span>
                          </div>
                        </td>
                        <td style={cellStyle(colors, columnWidths.unique_count)}>
                          <span style={{ fontWeight: '500', color: colors.text }}>{summary.unique_count.toLocaleString()}</span>
                        </td>
                        <td style={cellStyle(colors, columnWidths.completeness)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '60px',
                              height: '6px',
                              backgroundColor: isDarkMode ? '#1F1F23' : '#E5E7EB',
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: barsReady ? `${summary.completeness}%` : '0%',
                                height: '100%',
                                backgroundColor: summary.completeness >= 90 ? colors.success : 
                                                summary.completeness >= 70 ? colors.warning : colors.error,
                                borderRadius: '3px',
                                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                              }} />
                            </div>
                            <span style={{ fontSize: '12px', color: colors.text }}>{barsReady ? <AnimatedNumber value={summary.completeness} decimals={0} suffix="%" delay={0} duration={800} /> : '0%'}</span>
                          </div>
                        </td>
                        
                        {/* Quality Score Cell */}
                        <td style={cellStyle(colors, columnWidths.quality_score)}>
                          {summary.quality_score !== null && summary.quality_score !== undefined ? (
                            <div 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                position: 'relative'
                              }}
                              
                            >
                              <div style={{
                                width: '50px',
                                height: '6px',
                                backgroundColor: isDarkMode ? '#1F1F23' : '#E5E7EB',
                                borderRadius: '3px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: barsReady ? `${summary.quality_score}%` : '0%',
                                  height: '100%',
                                  backgroundColor: summary.quality_score >= 90 ? colors.success : 
                                                  summary.quality_score >= 70 ? colors.warning : 
                                                  summary.quality_score >= 50 ? colors.orange : colors.error,
                                  borderRadius: '3px',
                                  transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                                }} />
                              </div>
                              <span style={{ 
                                fontSize: '12px', 
                                fontWeight: '500',
                                color: summary.quality_score >= 90 ? colors.success : 
                                       summary.quality_score >= 70 ? colors.warning : 
                                       summary.quality_score >= 50 ? colors.orange : colors.error
                              }}>
                                {barsReady ? <AnimatedNumber value={summary.quality_score} decimals={0} suffix="%" delay={0} duration={800} /> : '0%'}
                              </span>
                              
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: colors.textMuted }}>N/A</span>
                          )}
                        </td>
                        
                        {showDetailedView && (
                          <>
                            {/* Issues Column - only in detailed view */}
                            <td style={cellStyle(colors, columnWidths.quality_issues)}>
                              {summary.quality_issues && summary.quality_issues.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {summary.quality_issues.slice(0, 3).map((issue, i) => (
                                    <span
                                      key={i}
                                      style={{
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        backgroundColor: isDarkMode ? '#374151' : '#FEE2E2',
                                        color: isDarkMode ? '#FCA5A5' : '#991B1B',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {issue}
                                    </span>
                                  ))}
                                  {summary.quality_issues.length > 3 && (
                                    <span 
                                      style={{ 
                                        fontSize: '11px', 
                                        color: colors.textMuted,
                                        padding: '2px 4px'
                                      }}
                                      title={summary.quality_issues.slice(3).join(', ')}
                                    >
                                      +{summary.quality_issues.length - 3} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ 
                                  fontSize: '11px', 
                                  color: colors.success,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  <CheckCircle size={12} />
                                  No issues
                                </span>
                              )}
                            </td>
                            <td style={cellStyle(colors, columnWidths.min)}>{formatValue(summary.min_value ?? summary.min_length ?? summary.min_date)}</td>
                            <td style={cellStyle(colors, columnWidths.max)}>{formatValue(summary.max_value ?? summary.max_length ?? summary.max_date)}</td>
                            <td style={cellStyle(colors, columnWidths.mean)}>{formatValue(summary.mean ?? summary.mean_length)}</td>
                            <td style={cellStyle(colors, columnWidths.median)}>{formatValue(summary.median)}</td>
                            <td style={cellStyle(colors, columnWidths.std_dev)}>{formatValue(summary.std_dev)}</td>
                            <td style={cellStyle(colors, columnWidths.q1)}>{formatValue(summary.q1)}</td>
                            <td style={cellStyle(colors, columnWidths.q3)}>{formatValue(summary.q3)}</td>
                            <td style={cellStyle(colors, columnWidths.iqr)}>{formatValue(summary.iqr)}</td>
                            <td style={cellStyle(colors, columnWidths.skewness)}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {getSkewnessIndicator(summary.skewness)}
                                <span style={{ color: colors.text }}>{formatValue(summary.skewness)}</span>
                              </div>
                            </td>
                            <td style={cellStyle(colors, columnWidths.normality)}>
                              {getNormalityIndicator(summary.normality_score)}
                            </td>
                          </>
                        )}
                        
                        <td style={{ ...cellStyle(colors, 50), textAlign: 'center' }}>
                          <button
                            onClick={() => toggleRowExpand(summary.variable_name)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              color: colors.textSecondary,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Row Details */}
                      {isExpanded && (
                        <tr>
                          <td 
                            colSpan={showDetailedView ? 19 : 8} 
                            style={{
                              padding: '20px 24px',
                              backgroundColor: isDarkMode ? '#0A0A0C' : '#F9FAFB',
                              borderBottom: `1px solid ${colors.border}`
                            }}
                          >
                            <ExpandedDetails summary={summary} colors={colors} formatValue={formatValue} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer */}
          <div style={{
            padding: '12px 16px',
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: colors.headerBg,
            fontSize: '12px',
            color: colors.textSecondary,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Showing {filteredSummaries.length} of {summaryData.summaries.length} variables</span>
            <span style={{ opacity: 0.7 }}>Drag column edges to resize</span>
          </div>
        </div>
        </AnimatedCard>
      )}
    </div>
  );
};

// Header Cell component with resize handle
const HeaderCell = ({ columnKey, width, children, sortConfig, onSort, onResizeStart, colors, sortable = true, isSticky = false }) => {
  const isActive = sortConfig?.key === columnKey;
  
  return (
    <th 
      data-column={columnKey}
      onClick={() => sortable && onSort && onSort(columnKey)}
      style={{
        position: 'sticky',
        top: 0,
        left: isSticky ? 0 : 'auto',
        zIndex: isSticky ? 30 : 20,
        padding: '14px 16px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '12px',
        color: colors.textSecondary,
        backgroundColor: colors.headerBg,
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        width: width,
        minWidth: width,
        maxWidth: width,
        borderBottom: `1px solid ${colors.border}`,
        borderRight: isSticky ? `1px solid ${colors.border}` : 'none',
        boxShadow: isSticky 
          ? `1px 0 0 0 ${colors.border}, 0 1px 0 0 ${colors.border}` 
          : `0 1px 0 0 ${colors.border}`
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
        <span>
          {children}
          {sortable && isActive && (
            <span style={{ marginLeft: '4px', color: colors.accent }}>
              {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </span>
        {/* Resize Handle */}
        <div
          className="resize-handle"
          onMouseDown={(e) => onResizeStart(e, columnKey)}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            zIndex: 30
          }}
        />
      </div>
    </th>
  );
};

// Helper component for expanded row details
const ExpandedDetails = ({ summary, colors, formatValue }) => {
  const isNumeric = summary.data_type === 'integer' || summary.data_type === 'float64';
  const isText = summary.data_type === 'string';
  const isDatetime = summary.data_type === 'timestamp';
  const textAnalytics = summary.text_analytics;
  const hasTextAnalytics = textAnalytics?.has_text_analytics;
  const histogramData = summary.histogram;

  // Get full value for tooltip
  const getFullValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
      if (isNaN(value) || !isFinite(value)) return String(value);
      return value.toLocaleString(undefined, { maximumFractionDigits: 10 });
    }
    return String(value);
  };

  const sectionStyle = {
    backgroundColor: colors.cardBg,
    borderRadius: '8px',
    padding: '16px',
    border: `1px solid ${colors.border}`,
    overflow: 'hidden'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(80px, 1fr) minmax(80px, 1.5fr)',
    gap: '8px',
    fontSize: '12px'
  };

  const labelStyle = { 
    color: colors.textSecondary,
    whiteSpace: 'nowrap'
  };
  
  const valueStyle = { 
    fontWeight: '500', 
    color: colors.text, 
    textAlign: 'right',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'help'
  };

  // Value cell component with tooltip
  const ValueCell = ({ value, decimals = 2 }) => {
    const formattedValue = formatValue(value, decimals);
    const fullValue = getFullValue(value);
    const needsTooltip = formattedValue !== fullValue && fullValue !== '—';
    
    return (
      <div 
        style={valueStyle} 
        title={needsTooltip ? fullValue : undefined}
      >
        {formattedValue}
      </div>
    );
  };

  // Sentiment bar component
  const SentimentBar = ({ positive, neutral, negative }) => (
    <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', marginTop: '8px' }}>
      <div style={{ width: `${positive}%`, backgroundColor: '#31ffffff' }} title={`Positive: ${positive}%`} />
      <div style={{ width: `${neutral}%`, backgroundColor: '#6B7280' }} title={`Neutral: ${neutral}%`} />
      <div style={{ width: `${negative}%`, backgroundColor: '#dc2626' }} title={`Negative: ${negative}%`} />
    </div>
  );

  // Keyword chip component
  const KeywordChip = ({ word, count, pct }) => (
    <span 
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        margin: '3px',
        backgroundColor: colors.border,
        borderRadius: '12px',
        fontSize: '11px',
        color: colors.text,
        cursor: 'help'
      }}
      title={`${count} occurrences (${pct}%)`}
    >
      {word}
    </span>
  );

  // Quality indicator with color
  const QualityIndicator = ({ value, total, label, threshold = 5 }) => {
    const safeValue = value ?? 0;
    const safeTotal = total ?? 0;
    const pct = safeTotal > 0 ? ((safeValue / safeTotal) * 100).toFixed(1) : 0;
    const isWarning = pct > threshold;
    return (
      <>
        <div style={labelStyle}>{label}</div>
        <div style={{ ...valueStyle, color: isWarning ? colors.warning : colors.text }}>
          {safeValue.toLocaleString()} ({pct}%)
        </div>
      </>
    );
  };

  // Histogram component for numeric variables
  const Histogram = ({ data, colors }) => {
    if (!data || !data.bins || data.bins.length === 0) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: colors.textSecondary,
          fontSize: '12px'
        }}>
          Insufficient data for histogram
        </div>
      );
    }

    const { bins, counts, bin_edges, stats } = data;
    const maxCount = Math.max(...counts);
    const totalCount = counts.reduce((a, b) => a + b, 0);
    const barWidth = 100 / bins.length;
    
    // Calculate positions for mean and median lines
    const range = bin_edges[bin_edges.length - 1] - bin_edges[0];
    const meanPosition = range > 0 ? ((stats.mean - bin_edges[0]) / range) * 100 : 50;
    const medianPosition = range > 0 ? ((stats.median - bin_edges[0]) / range) * 100 : 50;

    return (
      <div>
        {/* Histogram bars */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          height: '120px',
          gap: '2px',
          padding: '0 4px',
          position: 'relative'
        }}>
          {counts.map((count, idx) => {
            const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
            const pct = totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : 0;
            const binStart = bin_edges[idx];
            const binEnd = bin_edges[idx + 1];
            
            return (
              <div
                key={idx}
                style={{
                  flex: 1,
                  height: `${height}%`,
                  backgroundColor: '#2563EB',
                  borderRadius: '2px 2px 0 0',
                  minHeight: count > 0 ? '4px' : '0',
                  transition: 'height 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                title={`${formatValue(binStart, 2)} - ${formatValue(binEnd, 2)}\nCount: ${count.toLocaleString()} (${pct}%)`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#60A5FA';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563EB';
                }}
              />
            );
          })}
          
          {/* Mean line */}
          {meanPosition >= 0 && meanPosition <= 100 && (
            <div
              style={{
                position: 'absolute',
                left: `${meanPosition}%`,
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: '#10B981',
                zIndex: 10
              }}
              title={`Mean: ${formatValue(stats.mean, 4)}`}
            />
          )}
          
          {/* Median line */}
          {medianPosition >= 0 && medianPosition <= 100 && (
            <div
              style={{
                position: 'absolute',
                left: `${medianPosition}%`,
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: '#D97706',
                borderStyle: 'dashed',
                zIndex: 10
              }}
              title={`Median: ${formatValue(stats.median, 4)}`}
            />
          )}
        </div>
        
        {/* X-axis labels */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          fontSize: '10px',
          color: colors.textSecondary,
          marginTop: '4px',
          padding: '0 4px'
        }}>
          <span>{formatValue(bin_edges[0], 2)}</span>
          <span>{formatValue(bin_edges[Math.floor(bin_edges.length / 2)], 2)}</span>
          <span>{formatValue(bin_edges[bin_edges.length - 1], 2)}</span>
        </div>
        
        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: '16px',
          marginTop: '12px',
          fontSize: '11px'
        }}>
          <span>
            <span style={{ 
              display: 'inline-block', 
              width: '12px', 
              height: '3px', 
              backgroundColor: '#31ffffff',
              marginRight: '4px',
              verticalAlign: 'middle'
            }}></span>
            <span style={{ color: colors.textSecondary }}>Mean</span>
          </span>
          <span>
            <span style={{ 
              display: 'inline-block', 
              width: '12px', 
              height: '3px', 
              backgroundColor: '#D97706',
              marginRight: '4px',
              verticalAlign: 'middle',
              borderTop: '2px dashed #D97706',
              height: 0
            }}></span>
            <span style={{ color: colors.textSecondary }}>Median</span>
          </span>
        </div>
        
        {/* Distribution shape indicator */}
        {stats.skewness !== undefined && (
          <div style={{ 
            textAlign: 'center', 
            marginTop: '8px',
            fontSize: '11px',
            color: colors.textSecondary
          }}>
            {Math.abs(stats.skewness) < 0.5 ? (
              <span style={{ color: '#31ffffff' }}>Symmetric distribution</span>
            ) : stats.skewness > 0 ? (
              <span style={{ color: '#D97706' }}>Right-skewed (positive skew: {stats.skewness.toFixed(2)})</span>
            ) : (
              <span style={{ color: '#D97706' }}>Left-skewed (negative skew: {stats.skewness.toFixed(2)})</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
      {/* Basic Stats */}
      <div style={sectionStyle}>
        <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
          Basic Statistics
        </h4>
        <div style={gridStyle}>
          <div style={labelStyle}>Total Count</div>
          <ValueCell value={summary.total_count} />
          <div style={labelStyle}>Null Count</div>
          <div style={valueStyle}>{summary.null_count.toLocaleString()} ({summary.null_percentage}%)</div>
          <div style={labelStyle}>Unique Values</div>
          <ValueCell value={summary.unique_count} />
          <div style={labelStyle}>Non-Unique</div>
          <ValueCell value={summary.non_unique_count} />
          <div style={labelStyle}>Cardinality</div>
          <div style={valueStyle}>{(summary.cardinality * 100).toFixed(2)}%</div>
        </div>
      </div>

      {/* Histogram for Numeric Variables */}
      {isNumeric && histogramData && (
        <div style={{ ...sectionStyle, gridColumn: 'span 1' }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Distribution
            <span style={{ fontWeight: '400', fontSize: '11px', color: colors.textMuted, marginLeft: '8px' }}>
              ({histogramData.bins?.length || 0} bins)
            </span>
          </h4>
          <Histogram data={histogramData} colors={colors} />
        </div>
      )}

      {/* Numeric Stats */}
      {isNumeric && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Central Tendency
          </h4>
          <div style={gridStyle}>
            <div style={labelStyle}>Range</div>
            <div style={valueStyle} title={`${getFullValue(summary.min_value)} — ${getFullValue(summary.max_value)}`}>
              {formatValue(summary.min_value)} — {formatValue(summary.max_value)}
            </div>
            <div style={labelStyle}>Mean</div>
            <ValueCell value={summary.mean} decimals={4} />
            <div style={labelStyle}>Median</div>
            <ValueCell value={summary.median} decimals={4} />
            <div style={labelStyle}>Mode</div>
            <ValueCell value={summary.mode} />
            <div style={labelStyle}>Std Dev</div>
            <ValueCell value={summary.std_dev} decimals={4} />
            <div style={labelStyle}>CV</div>
            <div style={valueStyle} title={getFullValue(summary.coef_variation)}>
              {formatValue(summary.coef_variation)}%
            </div>
          </div>
        </div>
      )}

      {/* Quartiles */}
      {isNumeric && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Quartiles & Shape
          </h4>
          <div style={gridStyle}>
            <div style={labelStyle}>Q1 (25%)</div>
            <ValueCell value={summary.q1} decimals={4} />
            <div style={labelStyle}>Q2 (Median)</div>
            <ValueCell value={summary.median} decimals={4} />
            <div style={labelStyle}>Q3 (75%)</div>
            <ValueCell value={summary.q3} decimals={4} />
            <div style={labelStyle}>IQR</div>
            <ValueCell value={summary.iqr} decimals={4} />
            <div style={labelStyle}>Skewness</div>
            <ValueCell value={summary.skewness} decimals={4} />
            <div style={labelStyle}>Kurtosis</div>
            <ValueCell value={summary.kurtosis} decimals={4} />
            <div style={labelStyle}>Normality</div>
            <div style={valueStyle}>{summary.normality_interpretation || '—'}</div>
            <div style={labelStyle}>Outliers</div>
            <div style={{ ...valueStyle, color: summary.outlier_count > 0 ? colors.warning : colors.text }}>
              {summary.outlier_count?.toLocaleString() || 0}
            </div>
          </div>
        </div>
      )}

      {/* Histogram / Distribution */}
      {isNumeric && summary.histogram && (
        <div style={{ ...sectionStyle, gridColumn: 'span 2' }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Distribution
            <span style={{ fontWeight: '400', fontSize: '11px', color: colors.textMuted, marginLeft: '8px' }}>
              ({summary.histogram.total_count.toLocaleString()} values)
            </span>
          </h4>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px', padding: '0 4px' }}>
            {summary.histogram.counts.map((count, idx) => {
              const maxCount = summary.histogram.max_count;
              const heightPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
              const percentage = summary.histogram.percentages[idx];
              const binLabel = summary.histogram.bin_labels[idx];
              
              return (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    justifyContent: 'flex-end'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${heightPct}%`,
                      minHeight: count > 0 ? '2px' : '0',
                      backgroundColor: '#2563EB',
                      borderRadius: '2px 2px 0 0',
                      transition: 'height 0.3s ease',
                      cursor: 'pointer',
                      opacity: 0.8
                    }}
                    title={`${binLabel}\nCount: ${count.toLocaleString()}\n${percentage}%`}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                  />
                </div>
              );
            })}
          </div>
          {/* X-axis labels */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: '8px',
            fontSize: '10px',
            color: colors.textMuted,
            padding: '0 4px'
          }}>
            <span>{formatValue(summary.histogram.min_value)}</span>
            <span style={{ color: colors.textSecondary }}>
              {summary.histogram.bins} bins
            </span>
            <span>{formatValue(summary.histogram.max_value)}</span>
          </div>
          {/* Distribution stats */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '24px', 
            marginTop: '12px',
            fontSize: '11px'
          }}>
            <span>
              <span style={{ color: colors.textMuted }}>Mean: </span>
              <span style={{ color: '#31ffffff', fontWeight: '500' }}>{formatValue(summary.mean)}</span>
            </span>
            <span>
              <span style={{ color: colors.textMuted }}>Median: </span>
              <span style={{ color: '#D97706', fontWeight: '500' }}>{formatValue(summary.median)}</span>
            </span>
            <span>
              <span style={{ color: colors.textMuted }}>Mode: </span>
              <span style={{ color: '#8b6c9b', fontWeight: '500' }}>{formatValue(summary.mode)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Text Stats - Basic Metrics */}
      {isText && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Text Statistics
          </h4>
          <div style={gridStyle}>
            <div style={labelStyle}>Min Length</div>
            <div style={valueStyle}>{summary.min_length}</div>
            <div style={labelStyle}>Max Length</div>
            <div style={valueStyle}>{summary.max_length}</div>
            <div style={labelStyle}>Avg Length</div>
            <ValueCell value={summary.mean_length} />
            <div style={labelStyle}>Empty Strings</div>
            <div style={valueStyle}>{summary.empty_string_count}</div>
            {summary.detected_pattern && (
              <>
                <div style={labelStyle}>Pattern</div>
                <div style={valueStyle}>{summary.detected_pattern}</div>
              </>
            )}
            {/* Word count from text analytics */}
            {hasTextAnalytics && textAnalytics.basic_metrics && (
              <>
                <div style={labelStyle}>Avg Words</div>
                <div style={valueStyle}>{textAnalytics.basic_metrics.word_count_mean}</div>
                <div style={labelStyle}>Word Range</div>
                <div style={valueStyle}>
                  {textAnalytics.basic_metrics.word_count_min} — {textAnalytics.basic_metrics.word_count_max}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Text Analytics - Response Quality */}
      {isText && hasTextAnalytics && textAnalytics.quality_indicators && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Response Quality
          </h4>
          <div style={gridStyle}>
            <QualityIndicator 
              value={textAnalytics.quality_indicators.single_word_count} 
              total={textAnalytics.analyzed_count}
              label="Single Word"
              threshold={20}
            />
            <QualityIndicator 
              value={textAnalytics.quality_indicators.very_short_count} 
              total={textAnalytics.analyzed_count}
              label="Very Short (<3)"
              threshold={10}
            />
            <QualityIndicator 
              value={textAnalytics.quality_indicators.very_long_count} 
              total={textAnalytics.analyzed_count}
              label="Very Long"
              threshold={5}
            />
            <QualityIndicator 
              value={textAnalytics.quality_indicators.gibberish_count} 
              total={textAnalytics.analyzed_count}
              label="Gibberish"
              threshold={1}
            />
            <QualityIndicator 
              value={textAnalytics.quality_indicators.repeated_char_count} 
              total={textAnalytics.analyzed_count}
              label="Repeated Chars"
              threshold={1}
            />
          </div>
        </div>
      )}

      {/* Text Analytics - Survey Quality */}
      {isText && hasTextAnalytics && textAnalytics.survey_quality && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Survey Quality
          </h4>
          <div style={gridStyle}>
            <QualityIndicator 
              value={textAnalytics.survey_quality.dont_know_count} 
              total={textAnalytics.analyzed_count}
              label="DK/Refused"
              threshold={5}
            />
            <QualityIndicator 
              value={textAnalytics.survey_quality.duplicate_response_count} 
              total={textAnalytics.analyzed_count}
              label="Duplicates"
              threshold={10}
            />
            <QualityIndicator 
              value={textAnalytics.survey_quality.copy_paste_count} 
              total={textAnalytics.analyzed_count}
              label="Copy-Paste"
              threshold={2}
            />
          </div>
          {textAnalytics.survey_quality.dont_know_variants?.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: colors.textSecondary }}>
              <span>Variants: </span>
              {textAnalytics.survey_quality.dont_know_variants.slice(0, 5).map((v, i) => (
                <span key={i} style={{ 
                  backgroundColor: colors.border, 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  marginRight: '4px'
                }}>
                  {v}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text Analytics - Sentiment */}
      {isText && hasTextAnalytics && textAnalytics.sentiment?.available && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Sentiment Analysis
            {textAnalytics.sentiment.sampled && (
              <span style={{ fontWeight: '400', fontSize: '11px', color: colors.textMuted, marginLeft: '8px' }}>
                (sampled {textAnalytics.sentiment.analyzed_count.toLocaleString()})
              </span>
            )}
          </h4>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
            <span style={{ color: '#31ffffff' }}>Positive: {textAnalytics.sentiment.positive_pct}%</span>
            <span style={{ color: colors.textSecondary }}>Neutral: {textAnalytics.sentiment.neutral_pct}%</span>
            <span style={{ color: '#dc2626' }}>Negative: {textAnalytics.sentiment.negative_pct}%</span>
          </div>
          <SentimentBar 
            positive={textAnalytics.sentiment.positive_pct}
            neutral={textAnalytics.sentiment.neutral_pct}
            negative={textAnalytics.sentiment.negative_pct}
          />
          <div style={{ ...gridStyle, marginTop: '12px' }}>
            <div style={labelStyle}>Avg Score</div>
            <div style={{ 
              ...valueStyle, 
              color: textAnalytics.sentiment.avg_score > 0.1 ? '#31ffffff' : 
                     textAnalytics.sentiment.avg_score < -0.1 ? '#dc2626' : colors.text
            }}>
              {textAnalytics.sentiment.avg_score > 0 ? '+' : ''}{textAnalytics.sentiment.avg_score}
            </div>
            <div style={labelStyle}>Extreme Pos</div>
            <div style={valueStyle}>{textAnalytics.sentiment.extreme_positive_count}</div>
            <div style={labelStyle}>Extreme Neg</div>
            <div style={{ ...valueStyle, color: textAnalytics.sentiment.extreme_negative_count > 0 ? colors.warning : colors.text }}>
              {textAnalytics.sentiment.extreme_negative_count}
            </div>
          </div>
        </div>
      )}

      {/* Text Analytics - Content (Keywords) */}
      {isText && hasTextAnalytics && textAnalytics.content_analysis?.top_keywords?.length > 0 && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Top Keywords
            <span style={{ fontWeight: '400', fontSize: '11px', color: colors.textMuted, marginLeft: '8px' }}>
              ({textAnalytics.content_analysis.total_unique_words?.toLocaleString()} unique)
            </span>
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-3px' }}>
            {textAnalytics.content_analysis.top_keywords.slice(0, 12).map((kw, idx) => (
              <KeywordChip key={idx} word={kw.word} count={kw.count} pct={kw.pct} />
            ))}
          </div>
          {textAnalytics.content_analysis.language_detection_available && 
           textAnalytics.content_analysis.primary_language && (
            <div style={{ marginTop: '12px', fontSize: '12px' }}>
              <span style={{ color: colors.textSecondary }}>Language: </span>
              <span style={{ 
                color: colors.text, 
                fontWeight: '500',
                textTransform: 'uppercase'
              }}>
                {textAnalytics.content_analysis.primary_language}
              </span>
              {textAnalytics.content_analysis.is_multilingual && (
                <span style={{ 
                  marginLeft: '8px', 
                  padding: '2px 6px', 
                  backgroundColor: colors.warning + '20',
                  color: colors.warning,
                  borderRadius: '4px',
                  fontSize: '10px'
                }}>
                  Multilingual
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Text Analytics - Data Cleaning Insights */}
      {isText && hasTextAnalytics && textAnalytics.cleaning_insights && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Data Cleaning Insights
          </h4>
          <div style={gridStyle}>
            <QualityIndicator 
              value={textAnalytics.cleaning_insights.encoding_issues_count} 
              total={textAnalytics.analyzed_count}
              label="Encoding Issues"
              threshold={1}
            />
            <QualityIndicator 
              value={textAnalytics.cleaning_insights.html_markup_count} 
              total={textAnalytics.analyzed_count}
              label="HTML/Markup"
              threshold={1}
            />
            <div style={labelStyle}>Potential PII</div>
            <div style={{ 
              ...valueStyle, 
              color: textAnalytics.cleaning_insights.potential_pii_count > 0 ? '#dc2626' : colors.text 
            }}>
              {textAnalytics.cleaning_insights.potential_pii_count}
              {textAnalytics.cleaning_insights.pii_types_found?.length > 0 && (
                <span style={{ fontSize: '10px', marginLeft: '4px' }}>
                  ({textAnalytics.cleaning_insights.pii_types_found.join(', ')})
                </span>
              )}
            </div>
            <div style={labelStyle}>Whitespace Issues</div>
            <div style={valueStyle}>
              {textAnalytics.basic_metrics?.whitespace_issues_count || 0}
            </div>
          </div>
          {textAnalytics.cleaning_insights.standardization_candidates?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '6px' }}>
                Standardization Candidates:
              </div>
              {textAnalytics.cleaning_insights.standardization_candidates.slice(0, 3).map((c, idx) => (
                <div key={idx} style={{ 
                  fontSize: '11px', 
                  padding: '4px 8px',
                  backgroundColor: colors.border,
                  borderRadius: '4px',
                  marginBottom: '4px'
                }}>
                  <span style={{ color: colors.text, fontWeight: '500' }}>{c.canonical}</span>
                  <span style={{ color: colors.textSecondary }}> ({c.total_affected} records)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text Analytics - Character Composition (compact) */}
      {isText && hasTextAnalytics && textAnalytics.basic_metrics && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Character Composition
          </h4>
          <div style={{ display: 'flex', height: '20px', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
            <div 
              style={{ width: `${textAnalytics.basic_metrics.char_letters_pct}%`, backgroundColor: '#2563EB' }} 
              title={`Letters: ${textAnalytics.basic_metrics.char_letters_pct}%`}
            />
            <div 
              style={{ width: `${textAnalytics.basic_metrics.char_digits_pct}%`, backgroundColor: '#31ffffff' }} 
              title={`Digits: ${textAnalytics.basic_metrics.char_digits_pct}%`}
            />
            <div 
              style={{ width: `${textAnalytics.basic_metrics.char_special_pct}%`, backgroundColor: '#D97706' }} 
              title={`Special: ${textAnalytics.basic_metrics.char_special_pct}%`}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
            <span><span style={{ color: '#2563EB' }}>■</span> Letters {textAnalytics.basic_metrics.char_letters_pct}%</span>
            <span><span style={{ color: '#31ffffff' }}>■</span> Digits {textAnalytics.basic_metrics.char_digits_pct}%</span>
            <span><span style={{ color: '#D97706' }}>■</span> Special {textAnalytics.basic_metrics.char_special_pct}%</span>
          </div>
          <div style={{ ...gridStyle, marginTop: '12px' }}>
            <div style={labelStyle}>UPPERCASE</div>
            <div style={valueStyle}>{textAnalytics.basic_metrics.case_upper_pct}%</div>
            <div style={labelStyle}>lowercase</div>
            <div style={valueStyle}>{textAnalytics.basic_metrics.case_lower_pct}%</div>
            <div style={labelStyle}>Mixed Case</div>
            <div style={valueStyle}>{textAnalytics.basic_metrics.case_mixed_pct}%</div>
          </div>
        </div>
      )}

      {/* Top Values */}
      {summary.top_values && summary.top_values.length > 0 && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Top Values
          </h4>
          <div style={{ fontSize: '12px' }}>
            {summary.top_values.map((tv, idx) => (
              <div key={idx} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '6px 0',
                borderBottom: idx < summary.top_values.length - 1 ? `1px solid ${colors.borderLight}` : 'none'
              }}>
                <span style={{ 
                  maxWidth: '60%', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  color: colors.text
                }}>
                  {tv.value}
                </span>
                <span style={{ color: colors.textSecondary }}>
                  {tv.count.toLocaleString()} ({tv.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Datetime Stats */}
      {isDatetime && (
        <div style={sectionStyle}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: colors.text }}>
            Date Statistics
          </h4>
          <div style={gridStyle}>
            <div style={labelStyle}>Min Date</div>
            <div style={valueStyle}>{summary.min_date?.split('T')[0] || '—'}</div>
            <div style={labelStyle}>Max Date</div>
            <div style={valueStyle}>{summary.max_date?.split('T')[0] || '—'}</div>
            <div style={labelStyle}>Range (days)</div>
            <div style={valueStyle}>{summary.date_range_days?.toLocaleString() || '—'}</div>
            <div style={labelStyle}>Common Day</div>
            <div style={valueStyle}>{summary.most_common_day || '—'}</div>
            <div style={labelStyle}>Common Month</div>
            <div style={valueStyle}>{summary.most_common_month || '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Cell style helper
const cellStyle = (colors, width, isSticky = false, bgColor = 'transparent') => ({
  padding: '12px 16px',
  color: colors.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: width,
  minWidth: width,
  maxWidth: width,
  borderBottom: `1px solid ${colors.borderLight}`,
  position: isSticky ? 'sticky' : 'static',
  left: isSticky ? 0 : 'auto',
  zIndex: isSticky ? 10 : 'auto',
  backgroundColor: isSticky ? bgColor : 'transparent',
  borderRight: isSticky ? `1px solid ${colors.borderLight}` : 'none',
  boxShadow: isSticky ? `1px 0 0 0 ${colors.borderLight}` : 'none'
});

export default DataSummary;